import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { AlertTriangle, Download, DollarSign, RefreshCw, ShoppingCart, TrendingUp } from 'lucide-react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiClient from '@/services/api';
import type { OperationsReport, ReportMetrics, RevenueTrendPoint } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { Can } from '@/components/auth/Can';
import { PERM } from '@/constants/permissions';
import { buildSalesCsv, downloadCsv, printSalesReport } from '@/utils/reportExport';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

/** Default Sales-tab report: revenue, orders, trend, order-type mix, hourly revenue, top items. */
export default function SalesPerformanceReport({ filters }: ReportViewProps) {
  const {
    selectedRestaurantId,
    startDate,
    endDate,
    formatCurrency,
    selectedRestaurant,
  } = filters;

  const {
    data: revenueReport,
    isLoading: isLoadingRevenue,
    isError: isErrorRevenue,
    refetch: refetchRevenue,
  } = useQuery<ReportMetrics>({
    queryKey: ['revenue-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getRevenueReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const { data: trend, isLoading: isLoadingTrend } = useQuery<RevenueTrendPoint[]>({
    queryKey: ['revenue-trend', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getRevenueTrend(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const { data: operations } = useQuery<OperationsReport>({
    queryKey: ['operations-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getOperationsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const exportMeta = () => ({
    restaurantName: selectedRestaurant?.name ?? '',
    startDate,
    endDate,
    formatCurrency,
  });

  const handleExport = (exportFormat: 'PDF' | 'CSV') => {
    if (!revenueReport) return;
    const orderTypeMix = operations?.orderTypeMix;
    if (exportFormat === 'CSV') {
      downloadCsv(
        `sales-report-${format(new Date(), 'yyyy-MM-dd')}.csv`,
        buildSalesCsv(revenueReport, trend ?? [], orderTypeMix, exportMeta()),
      );
    } else {
      printSalesReport(revenueReport, trend ?? [], orderTypeMix, exportMeta());
    }
  };

  const trendChartData = (trend ?? []).map((p) => ({
    date: format(parseISO(p.date), 'MMM dd'),
    revenue: Number(p.revenue),
    orders: p.orders,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Sales Overview</h2>
          <p className="mt-1 text-sm text-gray-500">Revenue, orders, and trends across the selected date range.</p>
        </div>
        <Can name={PERM.REPORT_EXPORT}>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('CSV')}
              className="btn btn-outline flex items-center gap-2"
              disabled={!revenueReport}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('PDF')}
              className="btn btn-outline flex items-center gap-2"
              disabled={!revenueReport}
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </Can>
      </div>

      {!selectedRestaurantId ? (
        <div className="card">
          <div className="card-body text-center text-gray-500">
            Select a restaurant to view sales reports
          </div>
        </div>
      ) : isLoadingRevenue ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : isErrorRevenue ? (
        <div className="card"><div className="card-body p-8 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
          <p className="text-gray-900 font-medium mb-3">Failed to load the sales report</p>
          <button onClick={() => refetchRevenue()} className="btn btn-outline h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div></div>
      ) : revenueReport ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard
              label="Total Revenue"
              value={formatCurrency(revenueReport.revenue)}
              icon={DollarSign}
              palette="blue"
            />
            <KPICard
              label="Total Orders"
              value={Number(revenueReport.orders).toLocaleString()}
              icon={ShoppingCart}
              palette="green"
            />
            <KPICard
              label="Avg Order Value"
              value={formatCurrency(revenueReport.averageOrderValue)}
              icon={TrendingUp}
              palette="purple"
            />
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="text-lg font-semibold mb-4">Revenue Trend</h2>
              {isLoadingTrend ? (
                <div className="flex justify-center items-center h-72">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : trendChartData.length === 0 ? (
                <div className="text-center text-gray-500 py-12">No data in range</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="revenue" />
                    <YAxis yAxisId="orders" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Area
                      yAxisId="revenue"
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke="#0088FE"
                      fill="#0088FE"
                      fillOpacity={0.2}
                    />
                    <Bar yAxisId="orders" dataKey="orders" name="Orders" fill="#00C49F" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-body">
                <h2 className="text-lg font-semibold mb-4">Revenue by Order Type</h2>
                {(operations?.orderTypeMix?.length ?? 0) === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    No data in range
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={operations!.orderTypeMix}
                        dataKey="revenue"
                        nameKey="orderType"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {operations!.orderTypeMix.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h2 className="text-lg font-semibold mb-4">Revenue by Hour</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueReport.revenueByHour}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="revenue" fill="#0088FE" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="text-lg font-semibold mb-4">Top Selling Items</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity Sold
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {revenueReport.topSellingItems.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {Number(item.quantity).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(item.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center text-gray-500">No data available</div>
        </div>
      )}
    </div>
  );
}
